import { useState } from "react";

import { confirmEmailChange, startEmailChange } from "../../state/accountProfile";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";

type Step = "email" | "code" | "done";

/**
 * Two-step email change: enter the new address, then the six-digit code the
 * server mailed to it. The address is the sign-in identity, so a successful
 * confirm ends the current session — the dialog announces that and bounces
 * through `/pair`, which silently re-authenticates under the new address.
 */
export function ChangeEmailDialog({
  open,
  onOpenChange,
  currentEmail,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentEmail: string;
}) {
  const [step, setStep] = useState<Step>("email");
  const [newEmail, setNewEmail] = useState("");
  const [code, setCode] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setStep("email");
    setNewEmail("");
    setCode("");
    setIsBusy(false);
    setError(null);
  };

  const sendCode = async () => {
    setIsBusy(true);
    setError(null);
    const failure = await startEmailChange(newEmail.trim());
    setIsBusy(false);
    if (failure) {
      setError(failure);
      return;
    }
    setCode("");
    setStep("code");
  };

  const verify = async () => {
    setIsBusy(true);
    setError(null);
    const result = await confirmEmailChange(code.trim());
    setIsBusy(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setStep("done");
    // The old session is revoked server-side; /pair re-authenticates the
    // browser silently and lands back home under the new address.
    window.setTimeout(() => window.location.assign("/pair"), 1_800);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        // The done step is a forced hand-off to /pair — keep it visible.
        if (!nextOpen && step === "done") return;
        if (!nextOpen) reset();
        onOpenChange(nextOpen);
      }}
    >
      <DialogPopup className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change email</DialogTitle>
        </DialogHeader>
        <DialogPanel className="space-y-4">
          {step === "email" ? (
            <>
              <p className="text-sm text-muted-foreground">
                Your email is how you sign in. We’ll send a six-digit code to the new address to
                confirm it’s yours; the change only happens after you enter it.
              </p>
              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="change-email-new">
                  New email address
                </label>
                <Input
                  id="change-email-new"
                  type="email"
                  autoFocus
                  placeholder={currentEmail}
                  value={newEmail}
                  disabled={isBusy}
                  onChange={(event) => setNewEmail(event.currentTarget.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && newEmail.trim()) void sendCode();
                  }}
                />
              </div>
            </>
          ) : null}
          {step === "code" ? (
            <>
              <p className="text-sm text-muted-foreground">
                Enter the six-digit code we sent to{" "}
                <span className="font-medium text-foreground">{newEmail.trim()}</span>.
              </p>
              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="change-email-code">
                  Verification code
                </label>
                <Input
                  id="change-email-code"
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  disabled={isBusy}
                  onChange={(event) =>
                    setCode(event.currentTarget.value.replace(/\D/g, "").slice(0, 6))
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && code.length === 6) void verify();
                  }}
                  className="[&_[data-slot=input]]:tracking-[0.3em]"
                />
              </div>
              <button
                type="button"
                disabled={isBusy}
                className="cursor-pointer text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                onClick={() => void sendCode()}
              >
                Send a new code
              </button>
            </>
          ) : null}
          {step === "done" ? (
            <p className="text-sm text-muted-foreground">
              Email changed to{" "}
              <span className="font-medium text-foreground">{newEmail.trim()}</span>. Signing you
              back in…
            </p>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </DialogPanel>
        {step !== "done" ? (
          <DialogFooter>
            <Button variant="ghost" disabled={isBusy} onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {step === "email" ? (
              <Button disabled={isBusy || newEmail.trim().length === 0} onClick={() => void sendCode()}>
                {isBusy ? "Sending…" : "Send code"}
              </Button>
            ) : (
              <Button disabled={isBusy || code.length !== 6} onClick={() => void verify()}>
                {isBusy ? "Verifying…" : "Verify and change"}
              </Button>
            )}
          </DialogFooter>
        ) : null}
      </DialogPopup>
    </Dialog>
  );
}
